import React from "react";
import { ClipLoader } from "react-spinners";
import styled, { css } from "styled-components";

export type ButtonVariant = "primary" | "outline" | "ghost" | "white" | "social";
export type ButtonSize = "sm" | "md" | "lg";

const green = "#00863d";
const greenSoft = "#e7f4ec";
const greenSoft2 = "#d8eee1";
const line2 = "#e3e7ec";
const text = "#13131a";
const muted2 = "#a7b3c2";
const gradient = "linear-gradient(140deg, #2ec46f 0%, #00863d 100%)";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
	variant?: ButtonVariant;
	size?: ButtonSize;
	block?: boolean;
	loading?: boolean;
}

export const Button: React.FC<ButtonProps> = ({
	variant = "primary",
	size = "md",
	block = false,
	loading = false,
	disabled,
	children,
	...htmlProps
}) => {
	return (
		<StyledButton $variant={variant} $size={size} $block={block} disabled={disabled || loading} {...htmlProps}>
			{children}
			{loading && (
				<Loader $variant={variant}>
					<ClipLoader size={15} color={loaderColor(variant)} />
				</Loader>
			)}
		</StyledButton>
	);
};

function loaderColor(variant: ButtonVariant): string {
	return variant === "primary" ? "#ffffff" : green;
}

const sizes = {
	sm: css`
		height: 38px;
		padding: 0 16px;
		font-size: 13px;
	`,
	md: css`
		height: 48px;
		padding: 0 22px;
		font-size: 15px;
	`,
	lg: css`
		height: 52px;
		padding: 0 22px;
		font-size: 16px;
	`,
};

const variants = {
	primary: css`
		background: ${gradient};
		color: #ffffff;
		border: none;

		&:hover:not(:disabled) {
			filter: brightness(0.97);
		}
	`,
	outline: css`
		background: #ffffff;
		color: ${green};
		border: 1.5px solid ${green};

		&:hover:not(:disabled) {
			background: ${green};
			color: #ffffff;
		}
	`,
	ghost: css`
		background: ${greenSoft};
		color: ${green};
		border: none;

		&:hover:not(:disabled) {
			background: ${greenSoft2};
		}
	`,
	white: css`
		background: #ffffff;
		color: ${green};
		border: none;
		box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);

		&:hover:not(:disabled) {
			transform: translateY(-1px);
		}
	`,
	social: css`
		width: 100%;
		height: 48px;
		padding: 0 22px;
		gap: 10px;
		background: #ffffff;
		color: ${text};
		border: 1.5px solid ${line2};
		font-size: 14.5px;

		svg,
		img {
			width: 20px;
			height: 20px;
		}

		&:hover:not(:disabled) {
			background: #fafbfc;
			border-color: ${muted2};
		}
	`,
};

const StyledButton = styled.button<{ $variant: ButtonVariant; $size: ButtonSize; $block: boolean }>`
	box-sizing: border-box;
	position: relative;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	border-radius: 999px;
	font-weight: 600;
	font-family: inherit;
	white-space: nowrap;
	cursor: pointer;
	transition:
		transform 0.12s,
		filter 0.15s,
		background 0.15s,
		color 0.15s,
		border-color 0.15s;

	${({ $size, $variant }) => $variant !== "social" && sizes[$size]}
	${({ $variant }) => variants[$variant]}
	${({ $block, $variant }) =>
		$block &&
		$variant !== "social" &&
		css`
			width: 100%;
		`}

	&:active:not(:disabled) {
		transform: translateY(1px);
	}

	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}
`;

const Loader = styled.span<{ $variant: ButtonVariant }>`
	position: absolute;
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: inherit;
	background: ${({ $variant }) => ($variant === "primary" ? gradient : "#ffffff")};
`;
